import React, { useState, useEffect } from "react";
import { X, Mail, Clock, Play, Pause, Trash2, Check, CalendarClock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ScheduleModal = ({ series, onClose }) => {
  const [email, setEmail] = useState("");
  const [hour, setHour] = useState(7);
  const [minute, setMinute] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/delivery/schedule`, {
        email,
        series_slug: series.slug,
        delivery_hour: hour,
        delivery_minute: minute,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create schedule");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (h, m) => {
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={onClose}>
      <div
        className="bg-ff-surface border border-ff-border rounded-lg max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="schedule-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-ff-text-muted hover:text-ff-text">
          <X size={18} />
        </button>

        {success ? (
          <div className="text-center py-4" data-testid="schedule-success">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${series.accentColor}20` }}>
              <Check size={24} style={{ color: series.accentColor }} />
            </div>
            <h3 className="font-serif text-xl text-ff-text mb-2">You're all set</h3>
            <p className="text-ff-text-muted text-sm">
              Chapter 1 of <strong>{series.title}</strong> will arrive at{" "}
              <strong>{formatTime(hour, minute)} UTC</strong> tomorrow.
              One chapter per day until the story is complete.
            </p>
            <button onClick={onClose} className="mt-6 btn-primary px-6 py-2 text-sm" style={{ backgroundColor: series.accentColor }}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <CalendarClock size={20} style={{ color: series.accentColor }} />
              <div>
                <h3 className="font-serif text-lg text-ff-text">Daily Chapter Delivery</h3>
                <p className="text-ff-text-muted text-xs">{series.title} · {series.chapterCount} chapters</p>
              </div>
            </div>

            <p className="text-ff-text-muted text-sm mb-5">
              Get one chapter delivered to your inbox every day. Read the whole story at your own pace.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-ff-text-muted text-xs uppercase tracking-wider mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="schedule-email"
                  placeholder="you@example.com"
                  className="w-full input-dark rounded-sm"
                />
              </div>

              <div>
                <label className="block text-ff-text-muted text-xs uppercase tracking-wider mb-1.5">Delivery Time (UTC)</label>
                <div className="flex gap-2">
                  <select
                    value={hour}
                    onChange={(e) => setHour(parseInt(e.target.value))}
                    data-testid="schedule-hour"
                    className="flex-1 input-dark rounded-sm"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i === 0 ? "12 AM" : i < 12 ? `${i} AM` : i === 12 ? "12 PM" : `${i - 12} PM`}
                      </option>
                    ))}
                  </select>
                  <select
                    value={minute}
                    onChange={(e) => setMinute(parseInt(e.target.value))}
                    data-testid="schedule-minute"
                    className="w-24 input-dark rounded-sm"
                  >
                    <option value={0}>:00</option>
                    <option value={15}>:15</option>
                    <option value={30}>:30</option>
                    <option value={45}>:45</option>
                  </select>
                </div>
              </div>

              {error && <p className="text-red-400 text-sm" data-testid="schedule-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="schedule-submit"
                className="w-full py-2.5 text-sm font-semibold uppercase tracking-wider text-white transition-all hover:brightness-110 disabled:opacity-50"
                style={{ backgroundColor: series.accentColor, fontFamily: "'Montserrat', sans-serif" }}
              >
                <Mail size={14} className="inline mr-2" />
                {submitting ? "Setting up..." : "Start Daily Delivery"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};


const ManageSchedules = ({ email, onClose }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/api/delivery/schedules?email=${encodeURIComponent(email)}`);
      setSchedules(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSchedules(); }, [email]);

  const handlePause = async (id) => {
    await axios.post(`${API}/api/delivery/schedule/${id}/pause`);
    fetchSchedules();
  };

  const handleResume = async (id) => {
    await axios.post(`${API}/api/delivery/schedule/${id}/resume`);
    fetchSchedules();
  };

  const handleCancel = async (id) => {
    await axios.delete(`${API}/api/delivery/schedule/${id}`);
    fetchSchedules();
  };

  if (loading) return null;
  if (schedules.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="manage-schedules">
      {schedules.map((s) => (
        <div key={s.id} className="flex items-center justify-between p-3 border border-ff-border bg-ff-surface/50 rounded-sm">
          <div className="flex-1 min-w-0">
            <p className="text-ff-text text-sm font-medium truncate">{s.series_title}</p>
            <p className="text-ff-text-muted text-xs">
              {s.status === "completed"
                ? "Complete"
                : s.status === "paused"
                ? "Paused"
                : `Ch ${s.current_chapter} of ${s.total_chapters}`}
              {s.status === "active" && s.next_delivery_at && (
                <> · Next: {new Date(s.next_delivery_at).toLocaleString()}</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1 ml-2">
            {s.status === "active" && (
              <button onClick={() => handlePause(s.id)} className="p-1.5 text-ff-text-muted hover:text-amber-400 transition-colors" title="Pause">
                <Pause size={14} />
              </button>
            )}
            {s.status === "paused" && (
              <button onClick={() => handleResume(s.id)} className="p-1.5 text-ff-text-muted hover:text-green-400 transition-colors" title="Resume">
                <Play size={14} />
              </button>
            )}
            {s.status !== "completed" && (
              <button onClick={() => handleCancel(s.id)} className="p-1.5 text-ff-text-muted hover:text-red-400 transition-colors" title="Cancel">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export { ScheduleModal, ManageSchedules };
